import { makeStyles } from "@material-ui/core/styles";
import ErrorIcon from "@material-ui/icons/Error";
import {
  isAutobookingEnabled,
  isOnGoingAutobooking,
  isOrderForAutobooking
} from "@saleor/autobooking/utils/handlers";
import Skeleton from "@saleor/components/Skeleton";
import { MotherOrderList_orders_edges_node } from "@saleor/motherOrders/types/MotherOrderList";
import React from "react";

import ThreePlStatus from "../../ThreePlStatus";
import DeliveryStatusCell from "./DeliveryStatusCell";
import FailedStatusOrderTooltip from "./FailedStatusOrderTooltip";
import GrabBookingTime from "./GrabBookingTime";

const useStyles = makeStyles(
  {
    autobookingFailedReasonContainer: {
      alignItems: "center",
      color: "#EC5D77",
      display: "flex",
      fontSize: "16px",
      fontWeight: 600,
      justifyContent: "space-between",
      padding: "5px"
    },
    cellHolder: {
      alignItems: "center",
      color: "#6DAB64",
      display: "flex",
      fontSize: "17px",
      fontWeight: 600,
      justifyContent: "space-between",
      padding: "5px"
    },
    failedReasonIcon: {
      color: "#EC5D77"
    },
    failedStatusReasonIconTooltip: {
      alignItems: "flex-end",
      bottom: "35px",
      display: "none",
      justifyContent: "center",
      marginLeft: "5px",
      position: "absolute"
    },
    plStatus: {
      color: "#747C8C",
      fontWeight: 500
    },
    plStatusContainer: {
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center"
    },
    status3pl: {
      alignItems: "center",
      display: "flex"
    },
    status3plFailedReason: {
      "&:hover $failedStatusReasonIconTooltip": {
        display: "flex"
      },
      alignItems: "center",
      display: "flex",
      justifyContent: "center",
      padding: "0 5px",
      position: "relative"
    }
  },
  { name: "ThreePLCell" }
);

export interface ThreePLStatusCellProps {
  order: MotherOrderList_orders_edges_node;
  onGrabStatusRefresh: (orderId: string) => void;
  onPandaGoStatusRefresh: (orderId: string) => void;
  onRefreshList: () => void;
}

const ThreePLStatusCell: React.FC<ThreePLStatusCellProps> = props => {
  const {
    order,
    onGrabStatusRefresh,
    onRefreshList,
    onPandaGoStatusRefresh
  } = props;
  const classes = useStyles(props);

  const createdDateTime = new Date(order?.created);
  const nowDateTime = new Date();
  const bookingDateTime = new Date(order?.bookingTime);

  const [displayBookingMins, setDisplayBookingMins] = React.useState(false);

  React.useEffect(() => {
    setDisplayBookingMins(
      isOnGoingAutobooking(
        bookingDateTime,
        createdDateTime,
        nowDateTime,
        order?.bookingTaskId
      )
    );
  }, [order?.bookingTaskId]);

  const onTimerStops = () => {
    setTimeout(() => {
      onRefreshList();
      setDisplayBookingMins(
        isOnGoingAutobooking(
          bookingDateTime,
          createdDateTime,
          nowDateTime,
          order?.bookingTaskId
        )
      );
    }, 2000);
  };

  const getContentToRender = () => {
    let contentToRender = !order?.courier ? <Skeleton /> : null;

    if (order?.thirdPartyRider?.deliveryCourier?.status) {
      contentToRender = (
        <DeliveryStatusCell
          orderId={order.id}
          onGrabStatusRefresh={onGrabStatusRefresh}
          onPandaGoStatusRefresh={onPandaGoStatusRefresh}
          thirdPartyCourier={order?.courier}
          thirdPartyLogisticStatus={
            order?.thirdPartyRider?.deliveryCourier?.status
          }
        />
      );
    } else if (
      isAutobookingEnabled(
        order?.verticalType,
        order?.mall?.fbAutoBookingConfig?.isEnabled,
        order?.mall?.gmAutoBookingConfig?.isEnabled
      ) &&
      isOrderForAutobooking(order?.shippingMethod?.deliveryType) &&
      displayBookingMins
    ) {
      contentToRender = (
        <GrabBookingTime
          orderId={order.id}
          bookingDateTime={bookingDateTime}
          nowDateTime={nowDateTime}
          onTimerStops={onTimerStops}
        />
      );
    } else if (order?.deliveryCourier?.status) {
      contentToRender = (
        <div className={classes.plStatusContainer}>
          <div className={classes.status3pl}>
            <ThreePlStatus status={order.deliveryCourier.status} />
            {order.deliveryCourier.failedReason && (
              <div className={classes.status3plFailedReason}>
                <div className={classes.failedStatusReasonIconTooltip}>
                  <FailedStatusOrderTooltip
                    message={order.deliveryCourier.failedReason}
                    visible={true}
                  />
                </div>
                <ErrorIcon className={classes.failedReasonIcon} />
              </div>
            )}
          </div>
        </div>
      );
    } else if (order?.bookingFailedReason && !order?.courier) {
      contentToRender = (
        <div className={classes.autobookingFailedReasonContainer}>
          {"Autobooking Failed"}
        </div>
      );
    }

    return contentToRender;
  };

  return getContentToRender();
};

export default ThreePLStatusCell;
